import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { MessageCircle, Send, User, Clock, CheckCircle, XCircle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface Message {
  id: string;
  sender_id: string;
  sender_role: 'user' | 'admin';
  message: string;
  read: boolean;
  created_at: string;
}

interface Conversation {
  id: string;
  user_id: string;
  subject: string | null;
  status: string;
  last_message_at: string;
  created_at: string;
  profile?: {
    full_name: string | null;
    business_name: string | null;
  };
}

export function LiveChatAdmin() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('open');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadConversations();
    
    // Subscribe to new conversations
    const channel = supabase
      .channel('admin_chat_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations',
        },
        () => {
          loadConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [filter]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages();
      
      const channel = supabase
        .channel(`admin_chat_${selectedConversation.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `conversation_id=eq.${selectedConversation.id}`,
          },
          (payload) => {
            setMessages((prev) => [...prev, payload.new as Message]);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    let query = supabase
      .from('chat_conversations')
      .select('*')
      .order('last_message_at', { ascending: false });

    if (filter !== 'all') {
      query = query.eq('status', filter);
    }

    const { data, error } = await query;

    if (!error && data) {
      // Fetch user profiles for each conversation
      const conversationsWithProfiles = await Promise.all(
        data.map(async (conv) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name, business_name')
            .eq('id', conv.user_id)
            .single();
          
          return { ...conv, profile } as Conversation;
        })
      );
      setConversations(conversationsWithProfiles);
    }
  };

  const loadMessages = async () => {
    if (!selectedConversation) return;

    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', selectedConversation.id)
      .order('created_at', { ascending: true });

    if (!error && data) {
      setMessages(data as Message[]);
      
      // Mark user messages as read
      const unreadIds = data
        .filter((m) => !m.read && m.sender_role === 'user')
        .map((m) => m.id);
      
      if (unreadIds.length > 0) {
        await supabase
          .from('chat_messages')
          .update({ read: true })
          .in('id', unreadIds);
      }
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || !user) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('chat_messages').insert({
        conversation_id: selectedConversation.id,
        sender_id: user.id,
        sender_role: 'admin',
        message: newMessage.trim(),
      });

      if (error) throw error;

      await supabase
        .from('chat_conversations')
        .update({ last_message_at: new Date().toISOString() })
        .eq('id', selectedConversation.id);

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateConversationStatus = async (status: 'open' | 'closed') => {
    if (!selectedConversation) return;

    const { error } = await supabase
      .from('chat_conversations')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', selectedConversation.id);

    if (!error) {
      setSelectedConversation({ ...selectedConversation, status });
      loadConversations();
      toast({
        title: 'Success',
        description: `Conversation ${status === 'closed' ? 'closed' : 'reopened'}`,
      });
    }
  };

  const getUnreadCount = (convId: string) => {
    // This would need a separate query in production
    return 0;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
      {/* Conversations List */}
      <Card className="md:col-span-1">
        <CardHeader className="py-4">
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Conversations
            </span>
            <Badge variant="secondary">{conversations.length}</Badge>
          </CardTitle>
          <div className="flex gap-2 mt-2">
            {(['open', 'closed', 'all'] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className="capitalize"
              >
                {f}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[450px]">
            {conversations.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <MessageCircle className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No conversations</p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedConversation?.id === conv.id
                        ? 'bg-primary/10 border border-primary/20'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                          <User className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {conv.profile?.full_name || conv.profile?.business_name || 'Unknown User'}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      <Badge
                        variant={conv.status === 'open' ? 'default' : 'secondary'}
                        className="text-xs"
                      >
                        {conv.status}
                      </Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Chat Area */}
      <Card className="md:col-span-2">
        {selectedConversation ? (
          <>
            <CardHeader className="py-3 border-b flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">
                  {selectedConversation.profile?.full_name ||
                    selectedConversation.profile?.business_name ||
                    'Unknown User'}
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Started {format(new Date(selectedConversation.created_at), 'PPp')}
                </p>
              </div>
              <div className="flex gap-2">
                {selectedConversation.status === 'open' ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateConversationStatus('closed')}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Close
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateConversationStatus('open')}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Reopen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0 flex flex-col h-[480px]">
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender_role === 'admin' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-3 py-2 ${
                          msg.sender_role === 'admin'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="text-sm break-words">{msg.message}</p>
                        <p
                          className={`text-xs mt-1 ${
                            msg.sender_role === 'admin'
                              ? 'text-primary-foreground/70'
                              : 'text-muted-foreground'
                          }`}
                        >
                          {format(new Date(msg.created_at), 'HH:mm')}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {selectedConversation.status === 'open' && (
                <form onSubmit={sendMessage} className="p-3 border-t flex items-center gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    disabled={loading}
                    className="flex-1"
                  />
                  <Button type="submit" size="icon" disabled={loading || !newMessage.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              )}
            </CardContent>
          </>
        ) : (
          <CardContent className="h-full flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Select a conversation to view messages</p>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
